import React, { useEffect, useState } from 'react';
import { Card, Descriptions, Tag, Typography, Spin, Button, Divider, Space, Empty, Progress, Row, Col } from 'antd';
import { ArrowLeftOutlined, ExperimentOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { partnerAPI } from '../services/api';
import { buildChronologicalEvents } from '../components/ScheduleDetailModal';
import RequestTypeBadge from '../components/RequestTypeBadge';
import InquirySection from '../components/InquirySection';
import type { TimelineEvent } from '../components/ScheduleDetailModal';
import { useFillHeight } from '../hooks/useViewportFit';

const { Title, Text } = Typography;

const STATUS_COLORS: Record<string, string> = {
  '시작전': '#94a3b8',
  '처리완료': '#3b82f6', '조사완료': '#f59e0b', '성적서작성중': '#8b5cf6',
  '성적서완료': '#10b981', '발급완료': '#0ea5e9', '최종종료': '#64748b',
};

const TYPE_CFG = {
  '처리':    { color: '#f59e0b', label: '처리' },
  '약효조사': { color: '#10b981', label: '약효조사' },
  '약해조사': { color: '#8b5cf6', label: '약해조사' },
} as const;

// 상세페이지는 모달보다 여유 → 이벤트 열 62px / 커넥터 18px
const TL_ITEM = 62;
const TL_CONN = 18;
const TL_CONN_TOP = 30; // 날짜 20px + 도트행 24px/2 = 32 → -2 = 30

const HorizontalTimeline: React.FC<{ events: TimelineEvent[]; completedCount: number }> = ({ events, completedCount }) => (
  <div>
    <div style={{ overflowX: 'auto', paddingBottom: 2 }}>
      <div style={{ display: 'inline-flex', alignItems: 'flex-start' }}>
        {events.map((ev, idx) => {
          const isLast = idx === events.length - 1;
          const isPendingNext = !ev.completed && idx === completedCount;
          const isFirstPending = idx === completedCount;
          const cfg = TYPE_CFG[ev.type];

          const dotSize  = ev.completed ? 16 : isPendingNext ? 13 : 10;
          const dotColor = ev.completed ? cfg.color : isPendingNext ? '#f97316' : '#d1d5db';
          const connColor = ev.completed ? `${cfg.color}66` : '#e8ecf0';

          return (
            <React.Fragment key={ev.key}>
              {/* 완료→미완료 경계 */}
              {isFirstPending && completedCount > 0 && (
                <div style={{
                  width: 1, flexShrink: 0, background: '#e2e8f0',
                  alignSelf: 'stretch', marginLeft: 2, marginRight: 2, marginTop: 6,
                }} />
              )}

              {/* 이벤트 열 */}
              <div style={{ width: TL_ITEM, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {/* 날짜 */}
                <div style={{
                  height: 20, lineHeight: '20px', fontSize: 11, fontWeight: ev.completed ? 600 : 400,
                  color: ev.completed ? '#475569' : isPendingNext ? '#fb923c' : '#d1d5db',
                  textAlign: 'center', width: '100%',
                }}>
                  {ev.date ? ev.date.replace(/\d{4}-(\d{2})-(\d{2})/, '$1/$2') : '—'}
                </div>
                {/* 도트 */}
                <div style={{ height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{
                    width: dotSize, height: dotSize, borderRadius: '50%', background: dotColor,
                    boxShadow: isPendingNext
                      ? '0 0 0 5px rgba(249,115,22,0.15)'
                      : ev.completed ? `0 0 0 4px ${cfg.color}28` : 'none',
                  }} />
                </div>
                {/* 라벨 */}
                <div style={{ textAlign: 'center', marginTop: 5 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 8,
                    display: 'inline-block', whiteSpace: 'nowrap',
                    background: ev.completed ? cfg.color : isPendingNext ? '#fff7ed' : '#f8fafc',
                    color: ev.completed ? '#fff' : isPendingNext ? '#f97316' : '#b0bac6',
                    border: `1px solid ${ev.completed ? cfg.color : isPendingNext ? '#fed7aa' : '#e2e8f0'}`,
                  }}>
                    {cfg.label}
                  </span>
                  <div style={{ fontSize: 11, marginTop: 3, fontWeight: ev.completed ? 600 : 400, color: ev.completed ? '#334155' : isPendingNext ? '#f97316' : '#b0bac6' }}>
                    {ev.round}차
                  </div>
                  {!ev.completed && (
                    <div style={{ fontSize: 10, marginTop: 1, color: isPendingNext ? '#f97316' : '#cbd5e1' }}>
                      {isPendingNext ? '진행예정' : '미정'}
                    </div>
                  )}
                </div>
              </div>

              {/* 커넥터 — 고정 너비 */}
              {!isLast && (
                <div style={{
                  width: TL_CONN, height: 2, flexShrink: 0,
                  background: connColor,
                  marginTop: TL_CONN_TOP,
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>

    {/* 범례 */}
    <div style={{ display: 'flex', gap: 16, marginTop: 12, paddingTop: 12, borderTop: '1px solid #fef3c7', flexWrap: 'wrap', justifyContent: 'center' }}>
      {([
        { color: '#f59e0b', label: '처리' },
        { color: '#10b981', label: '약효조사' },
        { color: '#8b5cf6', label: '약해조사' },
        { color: '#f97316', label: '진행예정', pulse: true },
        { color: '#d1d5db', label: '미정' },
      ] as { color: string; label: string; pulse?: boolean }[]).map(item => (
        <Space key={item.label} size={4}>
          <div style={{ width: 11, height: 11, borderRadius: '50%', background: item.color, display: 'inline-block',
            boxShadow: item.pulse ? '0 0 0 3px rgba(249,115,22,0.2)' : 'none' }} />
          <Text style={{ fontSize: 11 }}>{item.label}</Text>
        </Space>
      ))}
    </div>
  </div>
);

const ScheduleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  /* 긴 상세 내용은 뷰포트 바닥까지 실측한 컨테이너 안에서만 스크롤 — 문서 세로스크롤 방지 */
  const { ref: fillRef, height: fillHeight } = useFillHeight(360);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const loadDetail = async () => {
    setLoading(true);
    try {
      const res = await partnerAPI.scheduleDetail(Number(id));
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getStatusColor = (status: string) => STATUS_COLORS[status] || 'default';

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return <Empty description="데이터를 불러올 수 없습니다." />;
  }

  const { events, completedCount } = buildChronologicalEvents(data);
  const tEvents  = events.filter(e => e.type === '처리');
  const yhEvents = events.filter(e => e.type === '약효조사');
  const ykEvents = events.filter(e => e.type === '약해조사');
  const filledT  = tEvents.filter(e => e.completed).length;
  const filledYh = yhEvents.filter(e => e.completed).length;
  const filledYk = ykEvents.filter(e => e.completed).length;

  return (
    <div ref={fillRef} style={{ height: fillHeight, overflowY: 'auto' }}>
      <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/schedules')} type="text" style={{ fontSize: 16 }} />
        <div style={{ flex: 1 }}>
          <Title level={4} style={{ margin: 0, color: '#92400e' }}>
            <ExperimentOutlined style={{ marginRight: 8 }} />{data.title}
          </Title>
          <Text type="secondary">{data.test_number}</Text>
        </div>
        <Tag color={getStatusColor(data.status)} style={{ fontSize: 14, padding: '4px 16px' }}>
          {data.status}
        </Tag>
      </div>

      {/* Test Info */}
      <Card size="small" style={{ marginBottom: 8 }}>
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small" bordered>
          <Descriptions.Item label="시험번호">{data.test_number}</Descriptions.Item>
          <Descriptions.Item label="항목명">{data.title}</Descriptions.Item>
          <Descriptions.Item label="시험년도">{data.test_year ? `${data.test_year}년` : '-'}</Descriptions.Item>
          <Descriptions.Item label="의뢰구분"><RequestTypeBadge value={data.request_type} /></Descriptions.Item>
          <Descriptions.Item label="시험구분">{data.category_name}</Descriptions.Item>
          <Descriptions.Item label="시험장소">{data.location_name}</Descriptions.Item>
          <Descriptions.Item label="포장주">{data.field_owner_name}</Descriptions.Item>
          <Descriptions.Item label="개시일">{data.start_date}</Descriptions.Item>
          <Descriptions.Item label="의뢰사">
            {(data.companies || []).length > 0
              ? (data.companies as any[]).map((c: any, i: number) => (
                  <span key={c.id || i} style={{ marginRight: 6, whiteSpace: 'nowrap' }}>
                    {c.code && <Tag color={c.is_primary ? 'orange' : 'default'} style={{ fontWeight: 600 }}>{c.code}</Tag>}
                    {c.name}
                  </span>
                ))
              : data.company_code
                ? <><Tag color="orange" style={{ fontWeight: 600 }}>{data.company_code}</Tag> {data.company_name}</>
                : data.company_name}
          </Descriptions.Item>
          <Descriptions.Item label="상태">
            <Tag color={getStatusColor(data.status)}>{data.status}</Tag>
          </Descriptions.Item>
          {data.field_address && (
            <Descriptions.Item label="시험포장주소" span={3}>{data.field_address}</Descriptions.Item>
          )}
          {data.planting_date && (
            <Descriptions.Item label="정식시기">{data.planting_date}</Descriptions.Item>
          )}
          {data.report_date && (
            <Descriptions.Item label="성적서발급일">{data.report_date}</Descriptions.Item>
          )}
          {data.tims_status && (
            <Descriptions.Item label="TIMS입력현황">{data.tims_status}</Descriptions.Item>
          )}
        </Descriptions>
        {/* 성적서사진유무는 내부 관리 항목 — 파트너 화면에서는 노출하지 않는다 (2026-08) */}
      </Card>

      {/* Progress Summary */}
      <Card size="small" style={{ marginBottom: 8, borderRadius: 12, background: '#fffbeb' }}>
        <Row gutter={16}>
          <Col span={8}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#92400e', marginBottom: 4 }}>처리 진행률</div>
              <Progress percent={tEvents.length ? Math.round((filledT / tEvents.length) * 100) : 0}
                size="small" strokeColor="#f59e0b" format={() => `${filledT}/${tEvents.length}`} />
            </div>
          </Col>
          <Col span={8}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#92400e', marginBottom: 4 }}>약효조사 진행률</div>
              <Progress percent={yhEvents.length ? Math.round((filledYh / yhEvents.length) * 100) : 0}
                size="small" strokeColor="#10b981" format={() => `${filledYh}/${yhEvents.length}`} />
            </div>
          </Col>
          <Col span={8}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#92400e', marginBottom: 4 }}>약해조사 진행률</div>
              <Progress percent={ykEvents.length ? Math.round((filledYk / ykEvents.length) * 100) : 0}
                size="small" strokeColor="#8b5cf6" format={() => `${filledYk}/${ykEvents.length}`} />
            </div>
          </Col>
        </Row>
      </Card>

      {/* Horizontal Timeline */}
      <Card title="시험 진행 타임라인" size="small" style={{ marginBottom: 8 }}>
        <HorizontalTimeline events={events} completedCount={completedCount} />
      </Card>

      {/* 이 시험에 대한 문의/요청 */}
      <InquirySection
        scheduleId={Number(id)}
        scheduleLabel={`${data.test_number || ''} · ${data.title || ''}`}
      />
    </div>
  );
};

export default ScheduleDetailPage;
